import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLock, FiBell, FiCreditCard, FiLink, FiCode, FiDatabase, FiCloud, FiSave, FiCheck } = FiIcons;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const { user, updateUser } = useAuthStore();
  
  const tabs = [
    { id: 'profile', label: 'Perfil', icon: FiUser },
    { id: 'security', label: 'Seguridad', icon: FiLock },
    { id: 'integrations', label: 'Integraciones', icon: FiLink },
    { id: 'notifications', label: 'Notificaciones', icon: FiBell },
    { id: 'billing', label: 'Facturación', icon: FiCreditCard }
  ];

  const ProfileTab = () => {
    const [formData, setFormData] = useState({
      name: user?.name || '',
      email: user?.email || '',
      company: user?.company || '',
      phone: user?.phone || '',
      position: user?.position || ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      updateUser(formData);
    };

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Información del Perfil</h2>
          <p className="text-gray-600">Actualiza tu información personal y de contacto</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Empresa</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cargo</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>Guardar Cambios</span>
            </button>
          </div>
        </form>
      </div>
    );
  };

  const IntegrationsTab = () => {
    const integrations = [
      {
        id: 'google',
        name: 'Google Workspace',
        description: 'Integra Drive, Calendar y Login con Google',
        icon: FiCloud,
        connected: false,
        services: [
          { name: 'Google Drive', enabled: false },
          { name: 'Google Calendar', enabled: false },
          { name: 'Login con Google', enabled: false }
        ]
      },
      {
        id: 'hubspot',
        name: 'HubSpot',
        description: 'Sincroniza candidatos con tu CRM',
        icon: FiDatabase,
        connected: false
      },
      {
        id: 'webhooks',
        name: 'Webhooks',
        description: 'Configura notificaciones en tiempo real',
        icon: FiLink,
        connected: false
      },
      {
        id: 'api',
        name: 'API',
        description: 'Accede a nuestra API REST',
        icon: FiCode,
        connected: false
      }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Integraciones</h2>
          <p className="text-gray-600">Conecta VIXEA con tus herramientas favoritas</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {integrations.map((integration) => (
            <div
              key={integration.id}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <SafeIcon icon={integration.icon} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{integration.name}</h3>
                    <p className="text-sm text-gray-600">{integration.description}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  {integration.connected ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      Conectado
                    </span>
                  ) : (
                    <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Conectar
                    </button>
                  )}
                </div>
              </div>

              {integration.services && (
                <div className="mt-4 space-y-2">
                  {integration.services.map((service) => (
                    <div
                      key={service.name}
                      className="flex items-center justify-between py-2 border-t border-gray-100"
                    >
                      <span className="text-sm text-gray-600">{service.name}</span>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={service.enabled}
                          className="sr-only peer"
                          onChange={() => {}}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-yellow-50 p-6 rounded-xl">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">Importante</h3>
          <p className="text-yellow-700">
            Integra Google Drive para asegurar que tus videos no se pierdan después de 15 días.
            Esta integración está disponible en todos los planes, incluyendo el gratuito.
          </p>
        </div>
      </div>
    );
  };

  const SecurityTab = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Seguridad</h2>
        <p className="text-gray-600">Configura la seguridad de tu cuenta</p>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Cambiar Contraseña</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña Actual</label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nueva Contraseña</label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar Nueva Contraseña</label>
            <input
              type="password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors">
            Actualizar Contraseña
          </button>
        </div>
      </div>
    </div>
  );

  const NotificationsTab = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Notificaciones</h2>
        <p className="text-gray-600">Configura cómo quieres recibir notificaciones</p>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
        {[
          'Nuevas entrevistas completadas',
          'Recordatorios de candidatos pendientes',
          'Actualizaciones del sistema',
          'Reportes semanales'
        ].map((notification, index) => (
          <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
            <span className="text-gray-900">{notification}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  const BillingTab = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Facturación</h2>
        <p className="text-gray-600">Gestiona tu plan y métodos de pago</p>
      </div>
      
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Plan Actual: Gratuito</h3>
            <p className="text-gray-600">5 entrevistas restantes este mes</p>
          </div>
          <button className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors">
            Actualizar Plan
          </button>
        </div>
        
        <div className="border-t border-gray-200 pt-6">
          <h4 className="text-md font-semibold text-gray-900 mb-4">Historial de Facturación</h4>
          <p className="text-gray-600">No hay facturas disponibles para el plan gratuito.</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configuración</h1>
        <p className="text-gray-600">Gestiona tu cuenta y preferencias</p>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar */}
        <div className="lg:w-64">
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-5 h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'profile' && <ProfileTab />}
            {activeTab === 'security' && <SecurityTab />}
            {activeTab === 'integrations' && <IntegrationsTab />}
            {activeTab === 'notifications' && <NotificationsTab />}
            {activeTab === 'billing' && <BillingTab />}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;